import { reportError, toErrorString } from '../utils/errorLogger.js';
import db from './db-service.js';
import { stripNul, symDecrypt, symEncrypt, tryGetSessionDEK } from '../helper/dekManager.js';
import { CACHE_VERSION, currentUser } from './at-rest.js';

/**
 * The mail cache is encrypted AT REST under the dMail DEK (helper/dekManager.js).
 *
 * It used to hold decrypted mail as plaintext, which was only survivable because sign-in deleted the
 * whole database every time — at the cost of re-prompting the wallet for every message, every session.
 * Now that the cache persists across sign-ins (routes/SignIn.js), plaintext would mean every message
 * the user ever opened sitting readable on disk forever. So: rows written from here are ciphertext
 * tagged with CACHE_VERSION, and the read path decrypts them.
 *
 * Two compatibility rules matter:
 *  - Rows without a cacheVersion are legacy plaintext and are still read (existing installs keep their
 *    cache; each row is rewritten as ciphertext the next time it is synced).
 *  - Rows are owner-tagged with userName, because mail ids are only unique per user. A row belonging to
 *    a different account is never served. Legacy rows carry no owner: they are treated as belonging to
 *    the current user, which is safe because sign-in still wipes the cache on any account switch.
 *
 * If the DEK cannot be resolved (auth not ready, endpoint down, wallet refused) the cache degrades to
 * a MISS — the caller falls back to the wallet decrypt path. It never falls back to storing plaintext.
 *
 * CACHE_VERSION and the owner tag are shared with db/at-rest.js, which applies this same envelope to the
 * non-mail stores (attachment / outbox / draft). One definition, so the two can never drift apart.
 */

/**
 * The cache key for a mail. Insecure / web2 mail ids are strings; chain mail ids are numeric. Reads MUST
 * derive the key exactly as writes do — `where('mailId').equals(5)` does not match a stored "5", so a
 * mismatch here is a silent 100% cache miss, not an error.
 */
const cacheKey = (id, type, message) =>
  type === 'Insecure' || message?.mailtype === 'web2emails' ? id : parseInt(id);

// Common transaction method for all the write operation
export const getMessageByIdAndType = async (id, type) => {
  const value = await db[`${type}`].where("mailId").equals(id).toArray();
  return value;
}

/**
 * Rows for `id` that belong to the signed-in user. Ownerless (pre-v2) rows are included — see the
 * owner-tagging note above.
 */
const getOwnedMessageByIdAndType = async (id, type) => {
  const rows = await getMessageByIdAndType(id, type);
  const user = currentUser();
  return rows.filter((row) => !row.userName || row.userName === user);
};

/** Decrypt a cached row back to its stored mail string, or null if it can't be read. */
async function readRow(row) {
  if (!row) return null;
  if (row.cacheVersion !== CACHE_VERSION) return row.decryptedMail; // legacy plaintext row
  const dek = await tryGetSessionDEK();
  if (!dek) return null;
  // stripNul is NOT optional: the WASM NUL-pads symDecrypt output, so without it JSON.parse below
  // throws, the bare catch swallows it, and every read degrades to a silent cache MISS — which
  // re-decrypts every message through the wallet and quietly undoes the whole point of the cache.
  // db/at-rest.js strips it; this path must match or the two envelopes drift apart.
  return stripNul(await symDecrypt(dek, row.decryptedMail));
}

export const getDecryptedContentFromIndexedDb = async (id, type) => {
  try {
    const value = await getOwnedMessageByIdAndType(id, type);
    const mail = await readRow(value[0]);
    return mail ? JSON.parse(mail) : null;
  } catch (error) {
    return null
  }
}

/** Record which account the cache belongs to, so sign-in can tell a re-login from an account switch. */
export async function setCacheOwner(userName) {
  if (!userName) return;
  await db.table('cacheMeta').put({ key: 'owner', userName });
}

export async function getCacheOwner() {
  try {
    const row = await db.table('cacheMeta').get('owner');
    return row?.userName || null;
  } catch {
    return null;
  }
}

export async function syncIndexedDB(id, msg, type, message) {

  try {
    if (type) {
      const pageTypeValue = type.toLowerCase();
      // const idformat = parseInt(id) ? parseInt(id) : id;
      const idformat = cacheKey(id, type, message);
      const user = currentUser();
      // No DEK means no cache write. Skipping costs a wallet decrypt next time; writing plaintext would
      // leave the message readable on disk permanently.
      const dek = await tryGetSessionDEK(user);
      if (!dek) return;
      const decryptedObject = {
        mailId: idformat,
        decryptedMail: await symEncrypt(dek, msg),
        type: pageTypeValue,
        userName: user,
        cacheVersion: CACHE_VERSION,
      };
      const value = await getMessageByIdAndType(decryptedObject.mailId, pageTypeValue);
      // Replace any row for this id — including a legacy plaintext row, and including one left behind by
      // a previous account, so a stale row can never shadow the row just written.
      const existing = value.find((row) => !row.userName || row.userName === user) || value[0];

      if (existing) {
        await db[`${pageTypeValue}`].update(existing.id, decryptedObject);
      } else {
        // create;
        await db.table(`${pageTypeValue}`).add(decryptedObject);
      }
      await setCacheOwner(user);
    }

  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: indexed-helper.js, Fun: syncIndexedDB, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.log(error);
  }
}

/**
 * Cached mail body as the raw stored string, or null on a miss. Call sites use this to skip the wallet
 * round-trip for a message that has already been decrypted once. Takes the same (id, type, message)
 * triple as syncIndexedDB so both sides agree on the key.
 */
export async function getCachedMail(id, type, message) {
  try {
    if (!type) return null;
    const rows = await getOwnedMessageByIdAndType(cacheKey(id, type, message), type.toLowerCase());
    return await readRow(rows[0]);
  } catch {
    return null;
  }
}

/**
 * Every key a mail with this `id` could have been cached under.
 *
 * syncIndexedDB stores the key via cacheKey(), which parseInt()s a chain mail id but leaves an Insecure /
 * web2 id as a string. The delete call sites (components/ContextMenu.js, components/MessageBoxHeader.js)
 * only have the RAW id — a string "5" or a BigInt 5n — and `where('mailId').equals("5")` does not match a
 * stored 5. That mismatch made the delete a silent no-op, which was survivable only while the cache was
 * plaintext-but-wiped-every-sign-in; now the cache persists, so a DELETED message's decrypted body would
 * have stayed on disk indefinitely.
 *
 * Rather than re-derive cacheKey() (the callers have no `message` to disambiguate with), match both
 * spellings. NaN is dropped: it is not a valid IndexedDB key and anyOf() would throw on it.
 */
function cacheKeyCandidates(id) {
  if (id === null || id === undefined) return [];
  const raw = typeof id === 'bigint' ? id.toString() : id;
  if (typeof raw !== 'string' && typeof raw !== 'number') return [];
  const keys = [raw];
  const numeric = parseInt(raw);
  if (!Number.isNaN(numeric) && numeric !== raw) keys.push(numeric);
  return keys;
}

export const removeMessageFromIndexedDb = async (id, type) => {
  const keys = cacheKeyCandidates(id);
  if (!type || !keys.length) return;
  await db[`${type}`].where("mailId").anyOf(keys).delete();
}
